import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import isValid from 'date-fns/isValid';

import { questionAnswered, questionErrored } from 'actions/questionnaires';
import { Mode } from 'common/questionnaires/types';

const QuestionDatePicker = ({ id, name, isForm, className, placeholder, popperPlacement, popperModifiers, isClearable, preventKeyboardDisplay, defaultDate, dateFormat, maxDate, mode, runValidation, dispatch }) => {
  const [startDate, setStartDate] = useState(null);
  const datePicker = React.useRef(null);

  const validateQuestion = () => {
    const dateFormatSchema = dateFormat && dateFormat.length > 0 ? dateFormat : 'MM dd, yyyy';
    const requiredFieldsComplete = (startDate !== null && isValid(moment(startDate, dateFormatSchema.toUpperCase()).toDate()));
    const date = moment(startDate).format(dateFormatSchema.toUpperCase());
    if (requiredFieldsComplete) {
      dispatch(questionAnswered({ id, answer: date, mode, isForm, name }));
    } else if (!requiredFieldsComplete && runValidation) {
      dispatch(questionErrored({ id, mode, isForm }));
    }
  };

  function isTouchDevice() {
    try {
      const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
      const mq = function (query) {
        return window.matchMedia(query).matches;
      };

      if (('ontouchstart' in window) || (typeof window.DocumentTouch !== 'undefined' && document instanceof window.DocumentTouch)) {
        return true;
      }

      return mq(['(', prefixes.join('touch-enabled),('), 'testtouch', ')'].join(''));
    } catch (e) {
      return false;
    }
  }

  function isIOS() {
    return !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
  }

  function onHandleKeyDown(e) {
    if (e.keyCode === 8) {
      setStartDate(null);
    }
  }

  function handleFocus(e) {
    if (isTouchDevice() && preventKeyboardDisplay && datePicker != null && datePicker.current != null && datePicker.current.input != null) {
      // Switching the reaadonly property of the input prevents the keyboard from display on touch devices.
      if (!datePicker.current.input.readOnly && isIOS()) {
        datePicker.current.input.blur();
      }
      datePicker.current.input.readOnly = true;
    }
  }

  function handleBlur(e) {
    if (isTouchDevice() && !isIOS() && preventKeyboardDisplay && datePicker != null && datePicker.current != null && datePicker.current.input != null) {
      // Switching the reaadonly property of the input prevents the keyboard from display on touch devices.
      datePicker.current.input.readOnly = false;
    }
  }

  useEffect(() => {
    validateQuestion();
  }, [startDate, runValidation]);

  return (
    <span id={ id }>
      <DatePicker
        id={ `${ id }-datepicker` }
        name={ name }
        ref={ datePicker }
        placeholder={ placeholder }
        fixedHeight
        showMonthDropdown
        placeholderText={ placeholder }
        maxDate={ maxDate }
        popperPlacement={ popperPlacement }
        popperModifiers={ popperModifiers }
        autoComplete="off"
        isClearable={ isClearable }
        showYearDropdown
        dropdownMode="select"
        className={ className === '' ? 'form-select' : className }
        dateFormat={ dateFormat && dateFormat.length > 0 ? dateFormat : 'MM-dd-yyyy' }
        selected={ startDate }
        onKeyDown={ e => onHandleKeyDown(e) }
        onChange={ (date) => { setStartDate(date); } }
        onFocus={ e => handleFocus(e) }
        onBlur={ e => handleBlur(e) }
        wrapperClassName="questionnaire-datepicker"
      />
    </span>
  );
};

QuestionDatePicker.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  isForm: PropTypes.bool,
  mode: PropTypes.number.isRequired,
  dispatch: PropTypes.func.isRequired,
  defaultDate: PropTypes.instanceOf(Date),
  runValidation: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  popperPlacement: PropTypes.string,
  popperModifiers: PropTypes.shape({}),
  preventKeyboardDisplay: PropTypes.bool,
  isClearable: PropTypes.bool,
  dateFormat: PropTypes.string,
  maxDate: PropTypes.instanceOf(Date),
};

QuestionDatePicker.defaultProps = {
  isForm: false,
  placeholder: 'Enter Date (MM-DD-YY)',
  popperPlacement: 'bottom-start',
  popperModifiers: {
    preventOverflow: {
      enabled: true,
      escapeWithReference: true,
      boundariesElement: 'viewport'
    }
  },
  className: '',
  isClearable: false,
  preventKeyboardDisplay: false,
  defaultDate: null,
  dateFormat: 'MM-dd-yyyy',
  runValidation: false,
  name: null,
  maxDate: new Date()
};

const mapDispatchToProps = dispatch => ({ dispatch });

const mapStateToProps = (state) => {
  const {
    app: { onlineConfig },
    formQuestionnaire: { runValidation }
  } = state;

  return {
    mode: (onlineConfig && onlineConfig.preQualifyingQuestionnaireMode) ? onlineConfig.preQualifyingQuestionnaireMode : Mode.Crs,
    runValidation
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(QuestionDatePicker);
